import { useRef } from "react";
import { useTranslation } from "react-i18next";
import { twJoin } from "tailwind-merge";

import type { Profile } from "@/api/bale.zod";

import { useGetProfileSuspense, useListEventInvitesSuspense } from "@/api/bale";
import { Missing } from "@/components/Missing";
import { SubtleButton } from "@/components/SubtleButton";
import { uniqueBy } from "@/fp/uniqueBy";
import { useCurrentEventSuspense } from "@/pages/EventPage/hooks/useCurrentEvent";

import { EventParticipant, type EventParticipantStyle } from "./EventParticipant";
import { ProfileListModal } from "./ProfileListModal";

export function PendingSection({ style }: { style: EventParticipantStyle }) {
  const { t } = useTranslation();

  const limitPending = style === "avatar-only" ? 20 : 5;

  const seeAllInvitedModal = useRef<HTMLDialogElement>(null);
  const seeAllDeclinedModal = useRef<HTMLDialogElement>(null);

  const me = useGetProfileSuspense("@me", { query: { select: (data) => data.data.profile } });
  function putMeFirst<T>(selector: (value: T) => Profile) {
    return (a: T, b: T) => {
      if (selector(a).id === me.data.id) return -1;
      if (selector(b).id === me.data.id) return 1;
      return 0;
    };
  }

  const event = useCurrentEventSuspense({ query: { select: (data) => data.data } });
  const sharing = useListEventInvitesSuspense(event.data.event.id, {
    query: { select: (data) => data.data.event_invites },
  });

  const nonParticipants = event.data.event_participants
    .filter((participant) => participant.response === "NotAttending")
    .toSorted(putMeFirst((val) => val.profile));
  const attendanceStates = new Map(
    event.data.event_participants.map((participant) => [participant.profile.id, participant.response]),
  );
  const pendingInvites = uniqueBy(
    (invite) => invite.invited.id,
    sharing.data.filter(
      (invite) => !attendanceStates.has(invite.invited.id) || attendanceStates.get(invite.invited.id) === "Ignored",
    ),
  ).toSorted(putMeFirst((val) => val.invited));

  return (
    <div className="flex grid-cols-2 flex-col gap-2 gap-y-4 @md:grid">
      <div className="flex flex-col gap-2">
        <h3 className="font-hint">{t("event-invited", { count: pendingInvites.length })}</h3>
        {pendingInvites.length === 0 && <Missing>{t("event-invited-empty")}</Missing>}
        <div className={twJoin("gap-2", style === "avatar-only" ? "flex flex-row flex-wrap" : "flex flex-col")}>
          {pendingInvites.slice(0, limitPending).map((invite) => (
            <EventParticipant profile={invite.invited} key={invite.invited.id} style={style} />
          ))}
        </div>
        {pendingInvites.length > limitPending && (
          <>
            <SubtleButton type="button" onClick={() => seeAllInvitedModal.current?.showModal()}>
              {t("see-all")}
            </SubtleButton>
            <ProfileListModal
              title={t("event-invited", { count: pendingInvites.length })}
              ref={seeAllInvitedModal}
              profiles={pendingInvites.map((invite) => invite.invited)}
              onClose={() => seeAllInvitedModal.current?.close()}
            />
          </>
        )}
      </div>
      <div className="flex flex-col gap-2">
        <h3 className="font-hint">{t("event-not-going", { count: nonParticipants.length })}</h3>
        {nonParticipants.length === 0 && <Missing>{t("event-not-going-empty")}</Missing>}
        <div className={twJoin("gap-2", style === "avatar-only" ? "flex flex-row flex-wrap" : "flex flex-col")}>
          {nonParticipants.slice(0, limitPending).map((participant) => (
            <EventParticipant profile={participant.profile} key={participant.profile.id} style={style} />
          ))}
        </div>
        {nonParticipants.length > limitPending && (
          <>
            <SubtleButton type="button" onClick={() => seeAllDeclinedModal.current?.showModal()}>
              {t("see-all")}
            </SubtleButton>
            <ProfileListModal
              title={t("event-not-going", { count: nonParticipants.length })}
              ref={seeAllDeclinedModal}
              profiles={nonParticipants.map((participant) => participant.profile)}
              onClose={() => seeAllDeclinedModal.current?.close()}
            />
          </>
        )}
      </div>
    </div>
  );
}
